/**
 * ClientContractsTab — Contracts and billing overview
 */

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { FileText, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import type { ClientContract } from "@/hooks/useAMClientDetail";

interface Props {
  contracts: ClientContract[];
}

const statusVariant: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  active: "default",
  paused: "secondary",
  completed: "outline",
  cancelled: "destructive",
};

export default function ClientContractsTab({ contracts }: Props) {
  const active = contracts.filter((c) => c.status === "active" || c.status === "paused");
  const historical = contracts.filter((c) => c.status !== "active" && c.status !== "paused");

  const renderContract = (c: ClientContract) => {
    const pct = c.total_leads_committed > 0
      ? Math.round((c.leads_delivered / c.total_leads_committed) * 100)
      : 0;
    const cycleStart = c.cycle_start ? format(new Date(c.cycle_start), "MMM d, yyyy") : "—";
    const cycleEnd = c.cycle_end ? format(new Date(c.cycle_end), "MMM d, yyyy") : "—";

    return (
      <Card key={c.id}>
        <CardContent className="pt-6 space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-medium capitalize text-sm">
                  {c.contract_type.replace(/_/g, " ")}
                </h3>
                <Badge variant={statusVariant[c.status] || "outline"} className="text-[10px]">
                  {c.status}
                </Badge>
                <Badge variant="outline" className="text-[10px]">{c.priority_tier}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1 font-mono">
                {c.id.slice(0, 8)}...
              </p>
            </div>
            <div className="flex items-center gap-1">
              {c.paused_at && (
                <Badge variant="secondary" className="text-[10px] flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Paused
                </Badge>
              )}
              {c.cancel_at_period_end && (
                <Badge variant="destructive" className="text-[10px]">
                  Cancelling at period end
                </Badge>
              )}
            </div>
          </div>

          {/* Delivery Progress */}
          <div>
            <div className="flex justify-between text-xs mb-1.5">
              <span className="text-muted-foreground">Delivery Progress</span>
              <span className="font-medium">{c.leads_delivered} / {c.total_leads_committed} leads ({pct}%)</span>
            </div>
            <Progress value={pct} className="h-2" />
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div>
              <p className="text-muted-foreground">Cycle Start</p>
              <p className="font-medium">{cycleStart}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Cycle End</p>
              <p className="font-medium">{cycleEnd}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Created</p>
              <p className="font-medium">{format(new Date(c.created_at), "MMM d, yyyy")}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Lead Types</p>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {c.lead_types && c.lead_types.length > 0 ? (
                  c.lead_types.map((lt) => (
                    <Badge key={lt} variant="outline" className="text-[10px] font-normal">
                      {lt}
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </div>
            </div>
          </div>

          {/* Paused Reason */}
          {c.paused_reason && (
            <div className="bg-muted/50 rounded-lg px-3 py-2 text-xs">
              <span className="text-muted-foreground">Pause reason: </span>
              {c.paused_reason}
            </div>
          )}

          {/* Notes */}
          {c.notes && (
            <div className="bg-muted/50 rounded-lg px-3 py-2 text-xs">
              <span className="text-muted-foreground">Notes: </span>
              {c.notes}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {contracts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No contracts found for this client.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {active.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">
                Active Contracts ({active.length})
              </h3>
              {active.map(renderContract)}
            </div>
          )}

          {historical.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">
                Historical ({historical.length})
              </h3>
              {historical.map(renderContract)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
